import { z } from 'zod';
import { uuid, isoTimestamp, syncColumns } from '../schemas/common.js';
import { AGENDA_PERSON_KINDS, AGENDA_PARTICIPANT_KINDS, AGENDA_SYNC_TABLES } from './enums.js';
import { recurrenceRule } from './recurrence.js';
/**
 * Row and protocol shapes for the agenda module.
 *
 * `household_id` is deliberately absent from every row schema, exactly as in the
 * groceries `rows.ts`: it is derived from the session server-side, never a
 * parameter a client sends.
 *
 * The agenda sync protocol reuses the platform's generic `pushResponse` /
 * `pullQuery` (they are protocol shapes, not groceries data) and declares its
 * own mutation union and pull response over the agenda tables.
 */
export const agendaSyncTable = z.enum(AGENDA_SYNC_TABLES);
/** A caregiver: a household member (`user`) or an external `person`. */
export const agendaParticipant = z.object({
    kind: z.enum(AGENDA_PARTICIPANT_KINDS),
    id: uuid,
});
/**
 * An activity's participant is always a household account — events are only for
 * the two of them (docs/AGENDA.md §3, 2026-08-19 feedback), never an external
 * `person`. Kept as its own shape (rather than reusing `agendaParticipant`) so
 * the schema itself refuses a `person` participant, not just the client UI.
 */
export const agendaActivityParticipant = z.object({
    kind: z.literal('user'),
    id: uuid,
});
/* ---------------- rows ---------------- */
export const agendaPersonRow = syncColumns.extend({
    id: uuid,
    name: z.string().min(1).max(120),
    kind: z.enum(AGENDA_PERSON_KINDS).default('caregiver'),
    color: z.string().max(20).nullish(),
});
/**
 * An activity — the one generic thing the adults do (no categories, docs
 * AGENDA.md §2). Anyone in the household may add or edit any activity; there is
 * no accept/decline handshake (docs §6) — `needs_car` is a flag, not a separate
 * object, and two overlapping car activities are a car conflict.
 */
export const agendaActivityRow = syncColumns.extend({
    id: uuid,
    title: z.string().min(1).max(200),
    starts_at: isoTimestamp,
    ends_at: isoTimestamp.nullish(),
    all_day: z.boolean().default(false),
    needs_car: z.boolean().default(false),
    /**
     * The parent takes the child along, so the activity itself covers the child
     * (docs AGENDA.md §5, 2026-08-20 feedback). Coverage treats a `brings_child`
     * activity as a caregiver window for its participants — no separate opvang row
     * is needed, and the activity never warns "waar is de kleine".
     */
    brings_child: z.boolean().default(false),
    notes: z.string().max(2000).nullish(),
    /** Which parent(s) — Jurren and/or Olga, never an external person; embedded so a whole-row LWW upsert stays atomic. */
    participants: z.array(agendaActivityParticipant).max(2).default([]),
    recurrence: recurrenceRule.nullish(),
    created_by: uuid.nullish(),
});
/**
 * A childcare period — its own timeline (docs AGENDA.md §3). **One or more
 * caregivers** (each a parent or an external person, 2026-08-20 feedback — an
 * afternoon can be grandma *and* the babysitter together). A null `ends_at`
 * means the **whole day**. An *absent* childcare row is not "uncovered" —
 * coverage is derived from activities vs. caregivers, so a gap is computed,
 * never stored.
 */
export const agendaChildcareRow = syncColumns.extend({
    id: uuid,
    /** At least one caregiver; the window is covered while any of them is on. */
    caregivers: z.array(agendaParticipant).min(1).max(8),
    starts_at: isoTimestamp,
    ends_at: isoTimestamp.nullish(),
    recurrence: recurrenceRule.nullish(),
    notes: z.string().max(2000).nullish(),
    created_by: uuid.nullish(),
});
/* ---------------- mutations ---------------- */
const agendaMutationId = z.object({ id: uuid });
const agendaUpsert = z.discriminatedUnion('table', [
    agendaMutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('agenda_people'),
        row: agendaPersonRow,
    }),
    agendaMutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('agenda_activities'),
        row: agendaActivityRow,
    }),
    agendaMutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('agenda_childcare'),
        row: agendaChildcareRow,
    }),
]);
/** A soft delete. Rows are never hard-deleted; the tombstone propagates. */
const agendaDelete = agendaMutationId.extend({
    op: z.literal('delete'),
    table: agendaSyncTable,
    row: z.object({ id: uuid, updated_at: isoTimestamp }),
});
export const agendaMutation = z.union([agendaUpsert, agendaDelete]);
export const agendaPushRequest = z.object({
    mutations: z.array(agendaMutation).max(500),
});
/** Same shape as the platform push response — applied ids + validation rejections. */
export const agendaPushResponse = z.object({
    applied: z.array(uuid),
    rejected: z.array(z.object({ id: uuid, code: z.string(), message: z.string() })),
    server_time: isoTimestamp,
});
export const agendaPullResponse = z.object({
    server_time: isoTimestamp,
    changes: z.object({
        agenda_people: z.array(z.record(z.unknown())),
        agenda_activities: z.array(z.record(z.unknown())),
        agenda_childcare: z.array(z.record(z.unknown())),
    }),
    has_more: z.boolean(),
    cursor: z.string().optional(),
    full_resync: z.boolean().optional(),
});
/* ---------------- read: the week view (docs AGENDA.md §1) ---------------- */
/**
 * `GET /api/v1/agenda/week`. The rows overlapping a week, which the client also
 * holds in its local mirror — this endpoint backs first paint, the contract
 * test and anything that wants the truth without a full sync. Occurrences are
 * expanded from these rows client-side (and in tests) via `expandWeek`.
 */
export const agendaWeekResponse = z.object({
    /** Monday's local date key. */
    week_start: z.string(),
    /** The seven local date keys, Monday first. */
    days: z.array(z.string()).length(7),
    people: z.array(agendaPersonRow),
    activities: z.array(agendaActivityRow),
    childcare: z.array(agendaChildcareRow),
});
/* ---------------- the change log (docs AGENDA.md §6) ---------------- */
/**
 * `GET /api/v1/agenda/changes`. A **derived** read, not a logged one — the same
 * pattern as the groceries activity feed (`shared/src/schemas/activity.ts`):
 * every event is a projection of a row the module already stores, told apart by
 * `created_at`/`updated_at`/`deleted_at`, never a write of its own.
 */
export const AGENDA_CHANGE_ENTITIES = ['agenda_activities', 'agenda_childcare', 'agenda_people'];
export const agendaChangeEntity = z.enum(AGENDA_CHANGE_ENTITIES);
export const AGENDA_CHANGE_VERBS = ['created', 'updated', 'deleted'];
export const agendaChangeVerb = z.enum(AGENDA_CHANGE_VERBS);
/**
 * One change. Everything below `summary` describes **the row as it stands now**,
 * not what the edit altered: this is a projection, so there is no previous
 * version to diff against and claiming one would be an invention. That is the
 * right answer anyway — "Zwemles" alone says nothing, and what the reader
 * actually needs is which slot in their week the other person just touched.
 */
export const agendaChangeEventDto = z.object({
    entity: agendaChangeEntity,
    id: uuid,
    verb: agendaChangeVerb,
    at: isoTimestamp,
    /** Named, never a device id — the log says "Sanne", the same as notifications. */
    actor: z.object({ id: uuid.nullable(), display_name: z.string() }),
    /** The headline: an activity's title, a caregiver's name, a person's name. */
    summary: z.string(),
    /** The window the row covers. Null on `agenda_people`, which has no time. */
    starts_at: isoTimestamp.nullable(),
    ends_at: isoTimestamp.nullable(),
    /** Whole-day: the activity's own flag, or childcare's null `ends_at` (§3). */
    all_day: z.boolean(),
    /** The row carries a recurrence rule, so it is one of a repeating series. */
    recurring: z.boolean(),
    /** Whose activity — resolved display names, empty for childcare and people. */
    participants: z.array(z.string()),
    /** The activity claims the car, which is the household's other scarce thing. */
    needs_car: z.boolean(),
});
export const agendaChangesResponse = z.object({ events: z.array(agendaChangeEventDto) });
//# sourceMappingURL=schemas.js.map